import type { CSSProperties, FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Button, Popover } from 'antd';
import type { ButtonProps, PopoverProps } from 'antd';
import { capitalCase } from 'change-case';
import { useHotkeys } from 'react-hotkeys-hook';
import { TbMessage2 } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useResourceCommentMutation } from '@/hooks/useResourceCommentMutation';
import { useWorkspaceResourceAccess } from '@/hooks/useWorkspaceResourceAccess';
import type {
    CommentComposerValue,
    ContextualCommentResource,
    ContextualCommentTarget,
    ResourceComment,
} from '@/types';
import { formatFinancialPeriodLabel } from '@/utilities';

import { SmallTooltip } from '../../tooltips';
import ContextualCommentComposer from '../ContextualCommentComposer';
import { isCommentableWorkspaceResource } from '../resourceCommentAccess';

interface ContextualCommentButtonProps {
    buttonStyle?: CSSProperties;
    buttonLabel?: ReactNode;
    buttonType?: ButtonProps['type'];
    disabled?: boolean;
    icon?: ReactNode;
    onCommentCreated?: (comment: ResourceComment) => void;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    placement?: PopoverProps['placement'];
    resource: ContextualCommentResource;
    shortcutActive?: boolean;
    target: ContextualCommentTarget;
    tooltipPlacement?: PopoverProps['placement'];
}

const CONTEXTUAL_COMMENT_OPEN_EVENT = 'countercyclical:contextual-comment-open';
let contextualCommentId = 0;

const ContextualCommentButton: FC<ContextualCommentButtonProps> = ({
    buttonStyle,
    buttonLabel,
    buttonType = 'text',
    disabled,
    icon,
    onCommentCreated,
    onOpenChange,
    open,
    placement = 'bottomRight',
    resource,
    shortcutActive = false,
    target,
    tooltipPlacement = 'top',
}) => {
    const { token } = useAntDesignTheme();
    const [internalOpen, setInternalOpen] = useState(false);
    const [composerId] = useState(() => `contextual-comment-${++contextualCommentId}`);
    const resourceAccess = useWorkspaceResourceAccess(
        isCommentableWorkspaceResource(resource.resourceType) ? resource.resourceType : 'investments',
        isCommentableWorkspaceResource(resource.resourceType) ? resource.resourceId : null,
    );
    const buttonDisabled =
        disabled || (isCommentableWorkspaceResource(resource.resourceType) && !resourceAccess.canComment);
    const composerOpen = open ?? internalOpen;
    const setComposerOpen = (nextOpen: boolean) => {
        const permittedOpen = buttonDisabled ? false : nextOpen;

        setInternalOpen(permittedOpen);
        onOpenChange?.(permittedOpen);

        if (permittedOpen) {
            window.dispatchEvent(new CustomEvent(CONTEXTUAL_COMMENT_OPEN_EVENT, { detail: { composerId } }));
        }
    };

    useEffect(() => {
        const closeWhenAnotherComposerOpens = (event: Event) => {
            const detail = (event as CustomEvent<{ composerId?: string }>).detail;

            if (detail?.composerId === composerId) {
                return;
            }

            setInternalOpen(false);
            onOpenChange?.(false);
        };

        window.addEventListener(CONTEXTUAL_COMMENT_OPEN_EVENT, closeWhenAnotherComposerOpens);

        return () => window.removeEventListener(CONTEXTUAL_COMMENT_OPEN_EVENT, closeWhenAnotherComposerOpens);
    }, [composerId, onOpenChange]);
    const { createComment, submitting } = useResourceCommentMutation({
        errorMessage: `Could not comment on ${target.label}`,
        onCreated: onCommentCreated,
        resourceHref: resource.resourceHref,
        resourceId: resource.resourceId,
        resourceType: resource.resourceType,
    });
    const selection = target.metadata?.selection as Record<string, unknown> | undefined;
    const configuredLineItems = Array.isArray(target.metadata?.lineItems) ? target.metadata.lineItems : [];
    const selectedLineItems = (Array.isArray(selection?.lineItems) ? selection.lineItems : configuredLineItems)
        .filter((lineItem): lineItem is string => typeof lineItem === 'string' && Boolean(lineItem.trim()))
        .map((lineItem) => capitalCase(lineItem));
    const selectionHasPeriod =
        selection?.kind === 'chart-range' ||
        typeof selection?.startLabel === 'string' ||
        typeof selection?.endLabel === 'string';
    const selectionStart = selectionHasPeriod ? (selection?.start ?? selection?.startLabel) : undefined;
    const selectionEnd = selectionHasPeriod ? (selection?.end ?? selection?.endLabel) : undefined;
    const selectionStartLabel = selectionStart === undefined ? '' : formatFinancialPeriodLabel(selectionStart);
    const selectionEndLabel = selectionEnd === undefined ? '' : formatFinancialPeriodLabel(selectionEnd);
    const selectionPeriod = selectionStartLabel
        ? selectionStartLabel === selectionEndLabel || !selectionEndLabel
            ? selectionStartLabel
            : `${selectionStartLabel} – ${selectionEndLabel}`
        : '';
    const metadataDescription = [target.type, selectedLineItems.join(', '), selectionPeriod]
        .filter(Boolean)
        .join(' · ');
    const configuredBreadcrumbs = Array.isArray(target.metadata?.breadcrumbs)
        ? target.metadata.breadcrumbs.filter(
              (breadcrumb): breadcrumb is string => typeof breadcrumb === 'string' && Boolean(breadcrumb.trim()),
          )
        : [];
    const resourceTitle = resource.resourceTitle?.trim();
    const targetLabel = target.label.trim();
    const shouldPrefixResource =
        resourceTitle &&
        resourceTitle.localeCompare(targetLabel, undefined, { sensitivity: 'accent' }) !== 0 &&
        !targetLabel.toLocaleLowerCase().startsWith(resourceTitle.toLocaleLowerCase());
    const headerBreadcrumbs = [shouldPrefixResource ? resourceTitle : undefined, ...configuredBreadcrumbs, targetLabel]
        .filter((breadcrumb): breadcrumb is string => Boolean(breadcrumb))
        .filter((breadcrumb, index, breadcrumbs) => breadcrumbs.indexOf(breadcrumb) === index);
    const headerLabel = headerBreadcrumbs.join(' · ');

    useHotkeys(
        'alt+shift+c',
        (event) => {
            event.preventDefault();
            setComposerOpen(true);
        },
        {
            enabled: shortcutActive && !buttonDisabled,
            enableOnContentEditable: false,
            enableOnFormTags: false,
            preventDefault: true,
        },
        [buttonDisabled, shortcutActive],
    );

    const submitComment = async ({ mentions, message }: CommentComposerValue) => {
        const comment = await createComment({
            component: target.component,
            mentions,
            message,
            metadata: {
                ...(target.metadata ?? {}),
                source: target.metadata?.source ?? 'contextual-comment',
                target: {
                    breadcrumbs: configuredBreadcrumbs,
                    component: target.component,
                    label: target.label,
                    selection: target.metadata?.selection,
                    type: target.type,
                    ...((target.metadata?.target as Record<string, unknown> | undefined) ?? {}),
                },
            },
        });

        if (!comment) {
            return false;
        }

        setComposerOpen(false);
        return true;
    };

    return (
        <Popover
            arrow={false}
            destroyOnHidden
            content={
                <ContextualCommentComposer
                    disabled={buttonDisabled}
                    headerLabel={headerLabel}
                    icon={icon}
                    metadataDescription={metadataDescription}
                    onSubmit={submitComment}
                    resourceId={resource.resourceId}
                    resourceType={resource.resourceType}
                    submitting={submitting}
                />
            }
            open={composerOpen}
            placement={placement}
            styles={{ body: { padding: 0 } }}
            trigger='click'
            onOpenChange={setComposerOpen}
        >
            <span onClick={(event) => event.stopPropagation()} onMouseDown={(event) => event.stopPropagation()}>
                <SmallTooltip title='Comment (⌥⇧C)' placement={tooltipPlacement}>
                    <Button
                        data-contextual-comment-trigger='true'
                        aria-label={`Comment on ${target.label}`}
                        disabled={buttonDisabled}
                        icon={<TbMessage2 style={{ color: token.colorTextTertiary }} />}
                        size='small'
                        style={{ ...buttonStyle }}
                        type={buttonType}
                        onClick={() => setComposerOpen(true)}
                    >
                        {buttonLabel}
                    </Button>
                </SmallTooltip>
            </span>
        </Popover>
    );
};

export default ContextualCommentButton;
